import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import 'src/styles/tbr.css';
import TBRLoginGate from 'src/components/tbr/TBRLoginGate';
import ThemeToggle from 'src/components/tbr/ThemeToggle';
import Drawer from 'src/components/tbr/Drawer';
import AddBookModal from 'src/components/tbr/AddBookModal';
import ConvertToSeriesModal from 'src/components/tbr/ConvertToSeriesModal';
import EditSeriesModal from 'src/components/tbr/EditSeriesModal';
import BookClub from 'src/components/tbr/BookClub';
import * as tbrApi from 'src/lib/tbrApi';

const { TBR_USERS } = tbrApi;

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

// Dev-only: admin bypass. Never runs in production builds.
const DEV_ADMIN = process.env.NODE_ENV === 'development';
const DEV_MOCK_BOOKS = DEV_ADMIN ? [
    {
        id: 'dev00001-0000-0000-0000-000000000001',
        title: 'The Name of the Wind',
        author: 'Patrick Rothfuss',
        type: 'book',
        addedBy: 'Jemma',
        addedAt: '2026-01-10',
        note: '',
        seriesBooks: [],
        purchasedBy: null,
        purchasedAt: null,
    },
    {
        id: 'dev00001-0000-0000-0000-000000000002',
        title: 'The Stormlight Archive',
        author: 'Brandon Sanderson',
        type: 'series',
        addedBy: 'Jemma',
        addedAt: '2026-02-14',
        note: 'Starting with book 1',
        seriesBooks: [
            { title: 'The Way of Kings', purchasedBy: null, purchasedAt: null },
            { title: 'Words of Radiance', purchasedBy: 'Jordyn', purchasedAt: '2026-03-01' },
            { title: 'Oathbringer', purchasedBy: null, purchasedAt: null },
        ],
        purchasedBy: null,
        purchasedAt: null,
    },
    {
        id: 'dev00001-0000-0000-0000-000000000003',
        title: 'Piranesi',
        author: 'Susanna Clarke',
        type: 'book',
        addedBy: 'Jordyn',
        addedAt: '2026-03-05',
        note: '',
        seriesBooks: [],
        purchasedBy: null,
        purchasedAt: null,
    },
] : [];

const DEV_MOCK_BOOKCLUB = DEV_ADMIN ? {
    currentBook: {
        title: 'The Thursday Murder Club',
        author: 'Richard Osman',
        coverId: 10527843,
        setAt: '2026-04',
    },
    nextMeeting: {
        date: '2026-06-15T18:00',
        location: "Kim's House",
    },
    vote: null,
    archive: [
        {
            title: 'Where the Crawdads Sing',
            author: 'Delia Owens',
            coverId: 9255275,
            finishedAt: '2026-03',
            avg: 4.3,
            ratings: [
                { member: 'Jemma',   stars: 5, comment: 'Absolutely loved it' },
                { member: 'Jordyn',  stars: 4, comment: 'Beautiful writing' },
                { member: 'Jaimie',  stars: 4, comment: '' },
                { member: 'Jessica', stars: 5, comment: 'Cried at the end' },
                { member: 'Kim',     stars: 3, comment: 'A bit slow in places' },
            ],
        },
        {
            title: 'The Midnight Library',
            author: 'Matt Haig',
            coverId: null,
            finishedAt: '2026-01',
            avg: 3.8,
            ratings: [
                { member: 'Jemma',   stars: 4, comment: '' },
                { member: 'Jordyn',  stars: 3, comment: 'Interesting concept' },
                { member: 'Kim',     stars: 4, comment: 'Really made me think' },
            ],
        },
    ],
} : null;

const DEV_BASE_VOTE = {
    wasFinished: true,
    round: 1,
    ratings: {
        Jemma:   { stars: 5, comment: 'Loved it!' },
        Jessica: { stars: 4, comment: '' },
        Jaimie:  { stars: 5, comment: 'Great read' },
        Kim:     { stars: 3, comment: 'A bit slow' },
        Jordyn:  { stars: 4, comment: 'Really enjoyed it' },
    },
    nominations: [
        { title: 'Fury Bound',      author: 'Sable Sorensen',    coverId: null,    nominatedBy: 'Jordyn',  votes: ['Jordyn'] },
        { title: 'The Night Circus', author: 'Erin Morgenstern', coverId: 8474036, nominatedBy: 'Jaimie',  votes: ['Jaimie'] },
        { title: 'Babel',           author: 'R.F. Kuang',        coverId: null,    nominatedBy: 'Jessica', votes: ['Jessica'] },
    ],
    nominationActed: ['Jordyn', 'Jaimie', 'Jessica'],
    recommendation: { title: 'Fury Bound', author: 'Sable Sorensen', wantedBy: ['Jordyn', 'Kim', 'Jaimie'] },
};

const DEV_VOTE_STAGES = DEV_ADMIN ? {
    'No Vote':    { ...DEV_MOCK_BOOKCLUB, vote: null },
    'Rating':     { ...DEV_MOCK_BOOKCLUB, vote: {
        ...DEV_BASE_VOTE,
        phase: 'rating',
        ratings: {},             // Jemma hasn't rated — rating modal shows
        nominations: [],
        nominationActed: [],
        hasActed: [],
    }},
    'Rate → Nom': { ...DEV_MOCK_BOOKCLUB, vote: {
        ...DEV_BASE_VOTE,
        phase: 'rating',
        ratings: { Jemma: { stars: 4, comment: 'Really enjoyed it' } }, // Jemma rated, others haven't
        nominations: [],
        nominationActed: [],     // Jemma hasn't nominated yet → nomination modal shows immediately
        hasActed: ['Jemma'],
    }},
    'Nominating': { ...DEV_MOCK_BOOKCLUB, currentBook: null, vote: {
        ...DEV_BASE_VOTE,
        phase: 'nominating',
        nominationActed: ['Jordyn', 'Jaimie', 'Jessica'], // Jemma hasn't nominated
        hasActed: ['Jordyn', 'Jaimie', 'Jessica'],
    }},
    'Nominated':  { ...DEV_MOCK_BOOKCLUB, currentBook: null, vote: {
        ...DEV_BASE_VOTE,
        phase: 'nominating',
        nominations: [
            ...DEV_BASE_VOTE.nominations,
            { title: 'Fourth Wing', author: 'Rebecca Yarros', coverId: 12800261, nominatedBy: 'Jemma', votes: ['Jemma'] },
        ],
        nominationActed: ['Jordyn', 'Jaimie', 'Jessica', 'Jemma'], // Jemma voted — change button + end vote button shows
        hasActed: ['Jordyn', 'Jaimie', 'Jessica', 'Jemma'],
    }},
    'Runoff':     { ...DEV_MOCK_BOOKCLUB, currentBook: null, vote: {
        ...DEV_BASE_VOTE,
        phase: 'runoff',
        nominationActed: ['Jordyn', 'Jaimie', 'Jessica', 'Jemma', 'Kim'],
        hasActed: [],            // Nobody has runoff voted yet — runoff modal shows
        round: 2,
        recommendation: null,
    }},
    'Winner':     { ...DEV_MOCK_BOOKCLUB, currentBook: null, vote: {
        ...DEV_BASE_VOTE,
        phase: 'done',
        nominationActed: ['Jordyn', 'Jaimie', 'Jessica', 'Jemma', 'Kim'],
        hasActed: ['Jordyn', 'Jaimie', 'Jessica', 'Jemma', 'Kim'],
        winner: { title: 'Fury Bound', author: 'Sable Sorensen', coverId: null },
    }},
} : null;

export default function TBRPage() {
    // Inject noindex so this route isn't indexed
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => document.head.removeChild(meta);
    }, []);

    const [theme, setTheme] = useState(() => localStorage.getItem('tbr_theme') || 'light');
    const [signedIn, setSignedIn] = useState(false);
    const [caller, setCaller] = useState(null);
    const [mirrorUser, setMirrorUser] = useState(null);
    const [books, setBooks] = useState([]);
    const [adding, setAdding] = useState(false);
    const [convertingBook, setConvertingBook] = useState(null);
    const [editingSeriesBook, setEditingSeriesBook] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [bookClub, setBookClub] = useState(null);
    const [dailyQuote, setDailyQuote] = useState(null);

    const isAdmin = TBR_USERS.find(u => u.name === caller)?.admin || false;
    const effectiveCaller = isAdmin && mirrorUser ? mirrorUser : caller;

    // Restore session on mount
    useEffect(() => {
        const token = tbrApi.getStoredToken();
        if (!token) return;
        const payload = tbrApi.decodeToken(token);
        if (!payload) { tbrApi.clearToken(); return; }
        setCaller(payload.name);
        setSignedIn(true);
    }, []);

    // Fetch lists and book club when signed in
    useEffect(() => {
        if (!signedIn) return;
        const token = tbrApi.getStoredToken();
        if (!token) return; // admin bypass — mock data already set in handleLogin
        setLoading(true);
        Promise.all([
            tbrApi.fetchLists(token),
            tbrApi.fetchBookClub(token),
        ])
            .then(([lists, club]) => { setBooks(lists); setBookClub(club); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [signedIn]);

    // Persist theme
    useEffect(() => {
        localStorage.setItem('tbr_theme', theme);
    }, [theme]);

    // Fetch weekly quote — once per week per user, cached in localStorage
    useEffect(() => {
        if (!signedIn || !caller) return;
        const today = new Date().toISOString().slice(0, 10);
        const cacheKey = `tbr_quote_${caller}`;
        const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { return null; } })();
        const daysSince = cached?.date ? (new Date(today) - new Date(cached.date)) / (1000 * 60 * 60 * 24) : 999;
        if (cached?.quote && daysSince < 7) { setDailyQuote(cached); return; }

        const token = tbrApi.getStoredToken();
        if (!token) return;

        tbrApi.fetchUserQuote(token)
            .then(data => {
                if (!data?.quote) return;
                localStorage.setItem(cacheKey, JSON.stringify(data));
                setDailyQuote(data);
            })
            .catch(() => {});
    }, [signedIn, caller]);

    function toggleTheme() {
        setTheme(t => t === 'light' ? 'dark' : 'light');
    }

    async function handleLogin(name, dob) {
        setLoginError('');
        if (DEV_ADMIN && name.toLowerCase() === 'admin') {
            setCaller('Jemma');
            setBooks(DEV_MOCK_BOOKS);
            setBookClub(DEV_MOCK_BOOKCLUB);
            setSignedIn(true);
            return;
        }
        try {
            const { name: canonicalName } = await tbrApi.login(name, dob);
            setCaller(canonicalName);
            setSignedIn(true);
        } catch (err) {
            if (err.status === 401) {
                setLoginError("That doesn't match. Check your name and date of birth.");
            } else {
                setLoginError('Something went wrong. Please try again.');
            }
        }
    }

    function handleSignOut() {
        tbrApi.clearToken();
        setSignedIn(false);
        setCaller(null);
        setMirrorUser(null);
        setBooks([]);
    }

    // Use a ref so reorder callback doesn't need books as a dep
    const booksRef = useRef(books);
    useEffect(() => { booksRef.current = books; }, [books]);

    const onClaim = useCallback((id, seriesIndex) => {
        // Optimistic update
        setBooks(bs => bs.map(b => {
            if (b.id !== id) return b;
            if (typeof seriesIndex === 'number') {
                return {
                    ...b,
                    seriesBooks: b.seriesBooks.map((c, i) =>
                        i === seriesIndex ? { ...c, purchasedBy: caller, purchasedAt: todayISO() } : c
                    ),
                };
            }
            return { ...b, purchasedBy: caller, purchasedAt: todayISO() };
        }));
        const token = tbrApi.getStoredToken();
        tbrApi.togglePurchase(token, id, seriesIndex)
            .then(updated => setBooks(bs => bs.map(b => b.id === id ? updated : b)))
            .catch(() => {});
    }, [caller]);

    const onUnclaim = useCallback((id, seriesIndex) => {
        // Optimistic update
        setBooks(bs => bs.map(b => {
            if (b.id !== id) return b;
            if (typeof seriesIndex === 'number') {
                return {
                    ...b,
                    seriesBooks: b.seriesBooks.map((c, i) =>
                        i === seriesIndex ? { ...c, purchasedBy: null, purchasedAt: null } : c
                    ),
                };
            }
            return { ...b, purchasedBy: null, purchasedAt: null };
        }));
        const token = tbrApi.getStoredToken();
        tbrApi.togglePurchase(token, id, seriesIndex)
            .then(updated => setBooks(bs => bs.map(b => b.id === id ? updated : b)))
            .catch(() => {});
    }, []);

    const onDelete = useCallback((id) => {
        setBooks(bs => bs.filter(b => b.id !== id));
        const token = tbrApi.getStoredToken();
        tbrApi.deleteBook(token, id).catch(() => {});
    }, []);

    const onReorder = useCallback((owner, dragId, overId) => {
        const bs = booksRef.current;
        const idxs = bs.map((b, i) => b.addedBy === owner ? i : -1).filter(i => i >= 0);
        const dragIdx = idxs.find(i => bs[i].id === dragId);
        const overIdx = idxs.find(i => bs[i].id === overId);
        if (dragIdx == null || overIdx == null || dragIdx === overIdx) return;
        const next = [...bs];
        const [moved] = next.splice(dragIdx, 1);
        const newOverIdx = next.findIndex(b => b.id === overId);
        next.splice(newOverIdx, 0, moved);
        setBooks(next);
        const token = tbrApi.getStoredToken();
        const ids = next.filter(b => b.addedBy === owner).map(b => b.id);
        tbrApi.reorderBooks(token, ids, isAdmin ? owner : undefined).catch(() => {});
    }, [isAdmin]);

    const onSelfStatus = useCallback((id, status, seriesIndex) => {
        // Optimistic update
        setBooks(bs => {
            const next = bs.map(b => {
                if (b.id !== id) return b;
                if (typeof seriesIndex === 'number') {
                    const updatedChildren = b.seriesBooks.map((c, i) =>
                        i === seriesIndex
                            ? { ...c, selfStatus: c.selfStatus === status ? 'tbr' : status }
                            : c
                    );
                    // Delete series locally if all read
                    if (updatedChildren.every(c => c.selfStatus === 'read')) return null;
                    return { ...b, seriesBooks: updatedChildren };
                }
                if (status === 'read') return null;
                return { ...b, selfStatus: b.selfStatus === status ? null : status };
            });
            return next.filter(Boolean);
        });
        const token = tbrApi.getStoredToken();
        tbrApi.updateSelfStatus(token, id, status, seriesIndex)
            .then(res => {
                if (!res?.deleted) setBooks(bs => bs.map(b => b.id === id ? res : b));
            })
            .catch(() => {});
    }, []);

    const onAdd = useCallback(async (entry) => {
        setAdding(false);
        const token = tbrApi.getStoredToken();
        try {
            const payload = isAdmin && mirrorUser ? { ...entry, targetUser: mirrorUser } : entry;
            const newEntry = await tbrApi.addBook(token, payload);
            setBooks(bs => [newEntry, ...bs]);
        } catch {}
    }, [isAdmin, mirrorUser]);

    const onConvertSave = useCallback(async ({ seriesName, author, absorbIds, newBooks }) => {
        const token = tbrApi.getStoredToken();
        try {
            const res = await tbrApi.convertToSeries(token, convertingBook.id, { seriesName, author, absorbIds, newBooks });
            setBooks(bs => bs
                .filter(b => !res.deletedIds.includes(b.id))
                .map(b => b.id === convertingBook.id ? res.series : b)
            );
        } catch {}
        setConvertingBook(null);
    }, [convertingBook]);

    const onEditSave = useCallback(async (id, fields) => {
        const token = tbrApi.getStoredToken();
        try {
            const updated = await tbrApi.updateBook(token, id, fields);
            setBooks(bs => bs.map(b => b.id === id ? updated : b));
        } catch {}
    }, []);

    const onEditSeriesSave = useCallback(async ({ title, author, seriesBooks }) => {
        const token = tbrApi.getStoredToken();
        try {
            const updated = await tbrApi.updateBook(token, editingSeriesBook.id, { title, author, seriesBooks });
            setBooks(bs => bs.map(b => b.id === editingSeriesBook.id ? updated : b));
        } catch {}
        setEditingSeriesBook(null);
    }, [editingSeriesBook]);

    const onCopy = useCallback(async (book) => {
        const token = tbrApi.getStoredToken();
        try {
            const entry = {
                title: book.title,
                author: book.author || '',
                type: book.type,
                seriesBooks: (book.seriesBooks || []).map(b => ({ title: b.title, purchasedBy: null, purchasedAt: null })),
                note: book.note || '',
                ...(isAdmin && mirrorUser ? { targetUser: mirrorUser } : {}),
            };
            const newEntry = await tbrApi.addBook(token, entry);
            setBooks(bs => [newEntry, ...bs]);
        } catch {}
    }, [isAdmin, mirrorUser]);

    const onBulkAdd = useCallback(async (entries) => {
        const token = tbrApi.getStoredToken();
        const added = [];
        for (const entry of entries) {
            try {
                const payload = isAdmin && mirrorUser ? { ...entry, targetUser: mirrorUser } : entry;
                const newEntry = await tbrApi.addBook(token, payload);
                added.push(newEntry);
            } catch {}
        }
        if (added.length) setBooks(bs => [...added.reverse(), ...bs]);
    }, [isAdmin, mirrorUser]);

    const orderedUsers = useMemo(() => {
        const nonAdmin = TBR_USERS.filter(u => !u.admin);
        if (!effectiveCaller) return nonAdmin;
        const you = nonAdmin.find(u => u.name === effectiveCaller);
        const rest = nonAdmin.filter(u => u.name !== effectiveCaller);
        return you ? [you, ...rest] : nonAdmin;
    }, [effectiveCaller]);

    const claimedCount = books.reduce((n, b) =>
        n + (b.type === 'series'
            ? b.seriesBooks.filter(c => c.purchasedBy).length
            : (b.purchasedBy ? 1 : 0)), 0);

    if (!signedIn) {
        return (
            <div className="tbr-page" data-theme={theme}>
                <ThemeToggle theme={theme} onToggle={toggleTheme} floating />
                <TBRLoginGate onLogin={handleLogin} error={loginError} />
            </div>
        );
    }

    return (
        <div className="tbr-page" data-theme={theme}>
            <header className="masthead">
                <div>
                    <h1 className="masthead-title">The J-Girls &amp; Ma TBR Index</h1>
                    <div className="masthead-sub">Private · No. 5 / Vol. I · {todayISO()}</div>
                </div>
                <div className="masthead-meta">
                    <ThemeToggle theme={theme} onToggle={toggleTheme} />
                    {isAdmin && (
                        <select
                            className="mirror-select"
                            value={mirrorUser || ''}
                            onChange={e => setMirrorUser(e.target.value || null)}
                            title="Mirror a user's perspective"
                        >
                            <option value="">Mirror: None</option>
                            {TBR_USERS.filter(u => !u.admin).map(u => (
                                <option key={u.name} value={u.name}>{u.name}</option>
                            ))}
                        </select>
                    )}
                    <span className="you-pill">
                        <span className="you-pill-mono">
                            {TBR_USERS.find(u => u.name === caller)?.initial || '?'}
                        </span>
                        <span>
                            Signed in as{' '}
                            <strong style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
                                {caller}
                            </strong>
                            {isAdmin && <span className="admin-badge">ADMIN</span>}
                        </span>
                    </span>
                    <button className="signout-btn" onClick={handleSignOut}>Sign out</button>
                </div>
            </header>

            {isAdmin && mirrorUser && (
                <div className="mirror-banner">
                    Mirroring <strong>{mirrorUser}</strong> — adds, edits, and reorders will be attributed to them
                </div>
            )}

            {dailyQuote && (
                <div className="daily-quote">
                    <span className="daily-quote-text">&ldquo;{dailyQuote.quote}&rdquo;</span>
                    {dailyQuote.author && (
                        <span className="daily-quote-author">— {dailyQuote.author}</span>
                    )}
                </div>
            )}

            {DEV_ADMIN && signedIn && (
                <div className="dev-stage-bar">
                    <span className="dev-stage-label">DEV stages:</span>
                    {Object.keys(DEV_VOTE_STAGES).map(stage => (
                        <button
                            key={stage}
                            className="dev-stage-btn"
                            onClick={() => setBookClub(DEV_VOTE_STAGES[stage])}
                        >
                            {stage}
                        </button>
                    ))}
                </div>
            )}

            <main className="tbr-main">
                {!loading && (
                    <BookClub data={bookClub} caller={effectiveCaller} isAdmin={isAdmin} onUpdate={setBookClub} />
                )}
                {loading ? (
                    <div className="tbr-loading">Loading…</div>
                ) : (
                    orderedUsers.map((u, i) => (
                        <Drawer
                            key={u.name}
                            user={u}
                            books={books}
                            caller={effectiveCaller}
                            onClaim={onClaim}
                            onUnclaim={onUnclaim}
                            onDelete={onDelete}
                            onSelfStatus={onSelfStatus}
                            onCopy={onCopy}
                            onEditSeries={book => setEditingSeriesBook(book)}
                            onConvertToSeries={book => setConvertingBook(book)}
                            onEditSave={onEditSave}
                            onAdd={() => setAdding(true)}
                            onReorder={onReorder}
                            defaultOpen={i === 0}
                        />
                    ))
                )}
            </main>

            <footer className="ledger">
                <span>5 readers</span>
                <span className="ledger-sep">·</span>
                <span>{books.length} entries</span>
                <span className="ledger-sep">·</span>
                <span>{claimedCount} claimed</span>
                <span className="ledger-sep">·</span>
                <span>circa 2026</span>
            </footer>

            {adding && (
                <AddBookModal
                    caller={effectiveCaller}
                    onClose={() => setAdding(false)}
                    onSave={onAdd}
                    onBulkSave={onBulkAdd}
                />
            )}

            {convertingBook && (
                <ConvertToSeriesModal
                    book={convertingBook}
                    myOtherBooks={books.filter(b => b.addedBy === effectiveCaller && b.type === 'book' && b.id !== convertingBook.id)}
                    onClose={() => setConvertingBook(null)}
                    onSave={onConvertSave}
                />
            )}

            {editingSeriesBook && (
                <EditSeriesModal
                    series={editingSeriesBook}
                    onClose={() => setEditingSeriesBook(null)}
                    onSave={onEditSeriesSave}
                />
            )}
        </div>
    );
}
